import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiChevronDown, FiChevronUp, FiSearch } from "react-icons/fi";
import GenericModal from "../components/GenericModal";
import api from "../../../src/api_interceptor/api";
import Loader from "../../components/loader/Loader";
import { sanitizeInput, sanitizeName } from "../../components/CommonFunction/CommonFunction";

const CLIENT_TYPES = [
  { id: 1, label: "Enterprise" },
  { id: 2, label: "Site" },
];

const getClientTypeLabel = (clientTypeId) => {
  const n = Number(clientTypeId);
  if (n === 1) return "Enterprise";
  if (n === 2) return "Site";
  return "Both";
};

const resolveActivityClientTypeId = (activity) => {
  const raw = activity?.ClientTypeId ?? activity?.clientTypeId;
  if (raw === null || raw === undefined || raw === "") return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
};

const RolesForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const hasFetched = useRef(false);
  const typeDropdownRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "",
    RoleTypeId: null,
    roleCode: "",
  });

  const [errors, setErrors] = useState({});
  const [isTypeOpen, setIsTypeOpen] = useState(false);
  const [roleTypeMaster, setRoleTypeMaster] = useState([]);
  const [activities, setActivities] = useState([]);
  const [searchActivity, setSearchActivity] = useState("");
  // 1 = Enterprise (default), 2 = Site — same as Activities list
  const [clientTypeId, setClientTypeId] = useState(1);
  // Ids allowed for the selected portal (from GetAllActivities — same filter as Activities page)
  const [allowedActivityIds, setAllowedActivityIds] = useState(null);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const [modalState, setModalState] = useState({
    open: false,
    title: "",
    description: "",
    type: "success", // success | error | warning
  });

  const showSuccess = (message) => {
    setModalState({
      open: true,
      title: "Success",
      description: message,
      type: "success",
    });
  };

  const showError = (message) => {
    setModalState({
      open: true,
      title: "Error",
      description: message || "Something went wrong. Please try again.",
      type: "error",
    });
  };

  // Load data for Add mode
  useEffect(() => {
    if (!isEditMode && !hasFetched.current) {
      hasFetched.current = true;
      getAddRoleData();
    }
  }, [isEditMode]);

  // Load data for Edit mode
  useEffect(() => {
    if (isEditMode && !hasFetched.current) {
      hasFetched.current = true;
      getEditRoleData();
    }
  }, [isEditMode, id]);

  // Load portal-filtered activity ids whenever Enterprise/Site toggle changes
  // (reuses the same GetAllActivities filter as the Activities page)
  useEffect(() => {
    let cancelled = false;

    const loadAllowedIds = async () => {
      try {
        const response = await api.post(
          "api/GetAllActivities",
          {
            filter: "",
            pageIndex: 0,
            totalPage: 1,
            pageLength: 10000,
            errorMessage: "",
            Type: "Activity",
            SortAction: "None",
            ClientTypeId: clientTypeId,
          },
          {
            headers: { "Content-Type": "application/json" },
            withCredentials: true,
          }
        );

        if (cancelled) return;

        const list = response?.data?.AllActivityList;
        if (Array.isArray(list)) {
          setAllowedActivityIds(new Set(list.map((a) => a.Id)));
        } else {
          setAllowedActivityIds(null);
        }
      } catch (error) {
        console.error("Load portal activities filter error:", error);
        if (!cancelled) {
          setAllowedActivityIds(null);
        }
      }
    };

    loadAllowedIds();
    return () => {
      cancelled = true;
    };
  }, [clientTypeId]);

  // Filter by Enterprise/Site + search
  const filteredActivities = useMemo(() => {
    const searchLower = searchActivity.trim().toLowerCase();

    return activities.filter((activity) => {
      const activityId = activity.ActivityId;
      const resolvedType = resolveActivityClientTypeId(activity);

      let matchesPortal = true;
      if (allowedActivityIds) {
        matchesPortal = allowedActivityIds.has(activityId);
      } else if (resolvedType != null) {
        matchesPortal = resolvedType === Number(clientTypeId);
      }

      if (!matchesPortal) return false;
      if (!searchLower) return true;

      return (
        activity.ActivityName?.toLowerCase().includes(searchLower) ||
        activity.ActivityCode?.toLowerCase().includes(searchLower) ||
        activity.ModuleName?.toLowerCase().includes(searchLower) ||
        activity.ModuleCode?.toLowerCase().includes(searchLower) ||
        getClientTypeLabel(resolvedType).toLowerCase().includes(searchLower)
      );
    });
  }, [activities, searchActivity, clientTypeId, allowedActivityIds]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        typeDropdownRef.current &&
        !typeDropdownRef.current.contains(event.target)
      ) {
        setIsTypeOpen(false);
      }
    };

    if (isTypeOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isTypeOpen]);

  const getAddRoleData = async () => {
    setIsLoading(true);
    try {
      const API_URL = "api/GetAllDetailsToAddRole";
      const req_body = {};

      const response = await api.post(API_URL, req_body, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response && response.data) {
        if (response.data.message === "Success" || response.data.ActivityList) {
          setActivities(response.data.ActivityList || []);
          setRoleTypeMaster(response.data.roleType || []);
        } else {
          showError(
            response.data.message || "Failed to load role data. Please try again."
          );
        }
      } else {
        showError("Invalid response from server. Please try again.");
      }
    } catch (error) {
      console.error("Get add role data error:", error);
      if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        showError(
          "Unable to connect to server. Please check your internet connection."
        );
      } else if (error.response) {
        const errorMsg =
          error.response.data?.Message ||
          "Failed to load role data. Please try again.";
        showError(errorMsg);
      } else {
        showError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getEditRoleData = async () => {
    setIsLoading(true);
    try {
      const API_URL = "api/GetAllDetailsToEditRoleForm";
      const req_body = {
        Id: parseInt(id),
      };

      const response = await api.post(API_URL, req_body, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response && response.data) {
        if (response.data.Message === "Success" || response.data.ID) {
          bindEditRole(response.data);
        } else {
          showError(
            response.data.Message || "Failed to load role details."
          );
        }
      } else {
        showError("Invalid response from server. Please try again.");
      }
    } catch (error) {
      console.error("Get edit role data error:", error);
      if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        showError(
          "Unable to connect to server. Please check your internet connection."
        );
      } else if (error.response) {
        const errorMsg =
          error.response.data?.Message ||
          "Failed to load role details. Please try again.";
        showError(errorMsg);
      } else {
        showError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const bindEditRole = (data) => {
    setFormData({
      name: data.Name || "",
      description: data.Description || "",
      type: data.roleType?.find((t) => t.id === data.RoleTypeId)?.name || "",
      RoleTypeId: data.RoleTypeId || null,
      roleCode: data.RoleCode || "",
    });

    if (data.ActivityList && Array.isArray(data.ActivityList)) {
      setActivities(data.ActivityList);
      const selected = data.ActivityList
        .filter((activity) => activity.IsSelected === true)
        .map((activity) => activity.ActivityId);
      setSelectedActivities(selected);
    }

    if (data.roleType && Array.isArray(data.roleType)) {
      setRoleTypeMaster(data.roleType);
    }
  };

  const handleTypeSelect = (roleType) => {
    setFormData((prev) => ({
      ...prev,
      type: roleType.name,
      RoleTypeId: roleType.id,
    }));
    setIsTypeOpen(false);
    setErrors((prev) => ({ ...prev, type: null }));
  };

  const handleActivityToggle = (activityId) => {
    setSelectedActivities((prev) =>
      prev.includes(activityId)
        ? prev.filter((x) => x !== activityId)
        : [...prev, activityId]
    );
    setErrors((prev) => ({ ...prev, activities: null }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = "Name is mandatory";
    }
    if (!formData.RoleTypeId) {
      newErrors.type = "Type is mandatory";
    }
    if (selectedActivities.length === 0) {
      newErrors.activities = "Select at least one activity";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitRole = async () => {
    setIsLoading(true);
    setSaving(true);

    const API_URL = "api/AddOrEditRole";
    const req_body = {
      Id: isEditMode ? parseInt(id) : 0,
      Name: formData.name.trim(),
      Description: formData.description?.trim() || "",
      RoleTypeId: formData.RoleTypeId,
      RoleCode: formData.roleCode || "",
      ProductCode: formData.roleCode || "",
      ActivityIds: selectedActivities,
    };

    try {
      const response = await api.post(API_URL, req_body, {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (response && response.data) {
        if (response.data.StatusCode === 1) {
          showSuccess(
            isEditMode
              ? "Role updated successfully."
              : "Role added successfully."
          );
          setShowSaveModal(false);
        } else if (response.data.StatusCode === 0) {
          const errorMsg =
            response.data.Message || "Failed to save role. Please try again.";
          showError(errorMsg);
        } else if (response.data.StatusCode === 3) {
          const errorMsg =
            response.data.Message ||
            "An error occurred while saving role. Please try again.";
          showError(errorMsg);
        } else {
          showError("Invalid response from server. Please try again.");
        }
      } else {
        showError("Invalid response from server. Please try again.");
      }
    } catch (error) {
      console.error("Submit role error:", error);

      if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        showError(
          "Unable to connect to server. Please check your internet connection."
        );
      } else if (error.response) {
        const errorMsg =
          error.response.data?.Message ||
          "Failed to save role. Please try again.";
        showError(errorMsg);
      } else {
        showError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
      setSaving(false);
      setShowSaveModal(false);
    }
  };

  const handleCancel = () => {
    setShowCancelModal(true);
  };

  const handleCancelConfirm = () => {
    navigate("/roles");
  };

  return (
    <>
      {isLoading && <Loader />}

      <div className="bg-white rounded-lg p-6">
        <div className="mb-4">
          <h1 className="text-xl font-semibold">
            {isEditMode ? "Edit Role" : "Add Role"}
          </h1>

          <nav className="text-sm mt-1">
            <span
              className="text-blue-500 cursor-pointer"
              onClick={() => navigate("/roles")}
            >
              Roles
            </span>
            <span className="mx-1 text-gray-400">&gt;</span>
            <span className="text-primary">
              {isEditMode ? formData.roleCode || "Edit Role" : "Add Role"}
            </span>
          </nav>
        </div>

        <div className="overflow-y-auto max-h-[calc(100vh-16rem)] pl-1 pr-6 custom-scrollbar">
          <div className="mb-8">
            <h2 className="text-base font-semibold text-primary mb-4">
              Section 1: Basic Details
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Code</label>
                <input
                  placeholder="Enter code"
                  value={isEditMode ? formData.roleCode || "--" : "--"}
                  disabled
                  className="w-full border rounded-md px-3 py-2 text-sm input-field"
                />
                <p className="text-xs mt-1">System generated code</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Name <span className="text-error">*</span>
                </label>
                <input
                  className={`w-full border rounded-md px-3 py-2 text-sm input-field ${
                    errors.name ? "input-error" : ""
                  }`}
                  placeholder="Enter role name"
                  value={formData.name}
                  maxLength={100}
                  onChange={(e) => {
                    const sanitized = sanitizeName(e.target.value);
                    setFormData({ ...formData, name: sanitized });
                    setErrors((prev) => ({ ...prev, name: null }));
                  }}
                />
                <div className="min-h-[16px]">
                  {errors.name && (
                    <p className="text-xs text-red-500 mt-1">{errors.name}</p>
                  )}
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-1">
                  Description
                </label>
                <textarea
                  rows={3}
                  maxLength={3000}
                  className="w-full border rounded-md px-3 py-2 text-sm input-field resize-none"
                  placeholder="Enter description"
                  value={formData.description}
                  onChange={(e) => {
                    const sanitized = sanitizeInput(e.target.value, {
                      allowNumbers: true,
                      allowLetters: true,
                      allowSpaces: true,
                      allowPunctuation: true,
                    });
                    setFormData({ ...formData, description: sanitized });
                  }}
                />
                <div className="text-right text-xs mt-1">3000 characters max</div>
              </div>

              <div className="relative" ref={typeDropdownRef}>
                <label className="block text-sm font-medium mb-1">
                  Type <span className="text-error">*</span>
                </label>

                <button
                  type="button"
                  disabled={isEditMode}
                  onClick={(e) => {
                    if (isEditMode) return;
                    e.stopPropagation();
                    setIsTypeOpen((prev) => !prev);
                  }}
                  className={`w-full border rounded-md px-3 py-2 text-sm flex justify-between items-center
                    ${errors.type ? "input-error" : ""}
                    ${isEditMode ? "bg-gray-100 cursor-not-allowed opacity-70" : ""}
                  `}
                >
                  <span
                    className={formData.type ? "text-black" : "text-gray-400"}
                  >
                    {formData.type || "- Select -"}
                  </span>

                  {!isEditMode &&
                    (isTypeOpen ? <FiChevronUp /> : <FiChevronDown />)}
                </button>

                {!isEditMode && isTypeOpen && (
                  <div className="absolute z-[9999] mt-1 w-full bg-white border rounded-md shadow-lg max-h-[200px] overflow-y-auto">
                    {roleTypeMaster.length === 0 ? (
                      <div className="px-4 py-2 text-sm text-gray-500">
                        No types available
                      </div>
                    ) : (
                      roleTypeMaster.map((type) => (
                        <div
                          key={type.id}
                          onClick={() => handleTypeSelect(type)}
                          className="px-4 py-2.5 text-sm cursor-pointer hover:bg-gray-100 border-b border-gray-200 last:border-b-0 whitespace-nowrap"
                        >
                          {type.name}
                        </div>
                      ))
                    )}
                  </div>
                )}

                <div className="min-h-[16px]">
                  {errors.type && (
                    <p className="text-xs text-red-500 mt-1">{errors.type}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <h2 className="text-base font-semibold text-primary mb-4">
              Section 2: Associated Activities
            </h2>

            <div className="flex flex-wrap items-center gap-4 mb-4">
              <div className="relative w-[260px]">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  placeholder="Search Activity"
                  className="border pl-9 pr-3 py-2 rounded text-sm input-field"
                  value={searchActivity}
                  onChange={(e) => setSearchActivity(e.target.value)}
                />
              </div>

              <div
                className="flex items-center gap-4"
                role="radiogroup"
                aria-label="Activity portal type"
              >
                {CLIENT_TYPES.map((type) => (
                  <label
                    key={type.id}
                    className="flex items-center gap-2 text-sm cursor-pointer select-none"
                  >
                    <input
                      type="radio"
                      name="roleActivityClientType"
                      className="accent-primary w-4 h-4"
                      value={type.id}
                      checked={Number(clientTypeId) === type.id}
                      onChange={() => setClientTypeId(type.id)}
                    />
                    <span>{type.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="border rounded-md overflow-hidden">
              <div className="max-h-[260px] overflow-y-auto table-scroll">
                <table className="w-full table-fixed">
                  <thead className="bg-light sticky top-0 z-10 border-b border-gray-200">
                    <tr>
                      <th className="w-[70px] px-3 py-2 text-center text-sm text-primary">
                        Assign
                      </th>
                      <th className="w-[120px] px-3 py-2 text-left text-sm text-primary">
                        Activity Code
                      </th>
                      <th className="w-[110px] px-3 py-2 text-left text-sm text-primary">
                        Module Code
                      </th>
                      <th className="w-[160px] px-3 py-2 text-left text-sm text-primary">
                        Module Name
                      </th>
                      <th className="w-[100px] px-3 py-2 text-left text-sm text-primary">
                        Type
                      </th>
                      <th className="px-3 py-2 text-left text-sm text-primary">
                        Activity Name
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredActivities.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="h-[120px] text-center text-sm text-gray-500"
                        >
                          No activities found
                        </td>
                      </tr>
                    ) : (
                      filteredActivities.map((activity) => (
                        <tr key={activity.ActivityId} className="border-b h-12">
                          <td className="text-center">
                            <input
                              className="cursor-pointer"
                              type="checkbox"
                              checked={selectedActivities.includes(
                                activity.ActivityId
                              )}
                              onChange={() =>
                                handleActivityToggle(activity.ActivityId)
                              }
                            />
                          </td>
                          <td className="px-3 text-sm">
                            {activity.ActivityCode}
                          </td>
                          <td className="px-3 text-sm">
                            {activity.ModuleCode || "-"}
                          </td>
                          <td className="px-3 text-sm">{activity.ModuleName}</td>
                          <td className="px-3 text-sm">
                            {getClientTypeLabel(
                              resolveActivityClientTypeId(activity)
                            )}
                          </td>
                          <td className="px-3 text-sm">{activity.ActivityName}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="min-h-[16px]">
              {errors.activities && (
                <p className="text-xs text-red-500 mt-2">{errors.activities}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <button
            className="w-[120px]"
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>

          <button
            className="btn-save w-[120px]"
            onClick={() => {
              if (!validateForm()) return;
              setShowSaveModal(true);
            }}
            disabled={saving}
          >
            {isEditMode ? "Update" : "Save"}
          </button>
        </div>

        <GenericModal
          open={showCancelModal}
          title="Confirm Cancel"
          description="Are you sure to cancel and exit?"
          primaryText="Yes"
          secondaryText="No"
          onPrimary={handleCancelConfirm}
          onClose={() => setShowCancelModal(false)}
          disableBackdropClose={false}
        />

        <GenericModal
          open={showSaveModal}
          title={isEditMode ? "Update Role" : "Save Role"}
          description={`Are you sure you want to ${
            isEditMode ? "update" : "save"
          } this role?`}
          primaryText={isEditMode ? "Update" : "Save"}
          secondaryText="Cancel"
          loading={saving}
          onPrimary={submitRole}
          onClose={() => {
            if (!saving) {
              setShowSaveModal(false);
            }
          }}
          disableBackdropClose={saving}
        />

        <GenericModal
          open={modalState.open}
          title={modalState.title}
          description={modalState.description}
          type={modalState.type}
          primaryText="OK"
          onPrimary={() => {
            const wasSuccess = modalState.type === "success";
            setModalState((prev) => ({ ...prev, open: false }));
            if (wasSuccess) {
              navigate("/roles");
            }
          }}
          onClose={() => {
            const wasSuccess = modalState.type === "success";
            setModalState((prev) => ({ ...prev, open: false }));
            if (wasSuccess) {
              navigate("/roles");
            }
          }}
        />
      </div>
    </>
  );
};

export default RolesForm;
